import { useEffect, useState, useCallback, useRef } from 'react';
import AdminLayout from '@/components/AdminLayout';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Plus, Trash2, Upload, ImageIcon, Save, ArrowUp, ArrowDown, Loader2, Eye, EyeOff, AlertCircle, RefreshCw,
} from 'lucide-react';

const CATEGORIES = ['Innenausbau', 'Renovierung', 'Fassade', 'Bodenarbeiten', 'Sonstiges'];

const AdminGallery = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const fileInputRef = useRef(null);
  const replaceTargetId = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api.get('/admin/gallery');
      if (!Array.isArray(res.data)) {
        throw new Error('Backend lieferte ungueltiges Datenformat (Array erwartet)');
      }
      setItems(res.data);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || e.message || 'Unbekannter Fehler';
      const url = e.config?.baseURL ? `${e.config.baseURL}${e.config.url}` : '/admin/gallery';
      let errorMessage;
      if (status === 401) {
        errorMessage = 'Nicht angemeldet (401). Bitte erneut einloggen.';
      } else if (status === 404) {
        errorMessage = `API-Endpoint nicht gefunden (404) - ${url}. Wahrscheinlich ist die neueste Version noch nicht deployed.`;
      } else if (status === 500) {
        errorMessage = `Server-Fehler (500): ${detail}`;
      } else if (!status) {
        errorMessage = `Netzwerk-/CORS-Fehler: ${detail} (URL: ${url})`;
      } else {
        errorMessage = `HTTP ${status}: ${detail}`;
      }
      // Vollstaendiger Log fuer DevTools
      // eslint-disable-next-line no-console
      console.error('[AdminGallery] Load failed:', { status, detail, url, error: e });
      setLoadError({ message: errorMessage, status, detail, url });
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateLocal = (id, patch) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  };

  const persist = async (id) => {
    const item = items.find((i) => i.id === id);
    if (!item) return;
    setSavingId(id);
    try {
      const res = await api.put(`/admin/gallery/${id}`, {
        title: item.title,
        description: item.description,
        alt_text: item.alt_text,
        category: item.category,
        order: parseInt(item.order, 10),
        active: item.active,
      });
      updateLocal(id, res.data);
      toast.success('Gespeichert');
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(`Speichern fehlgeschlagen: ${detail}`);
    } finally {
      setSavingId(null);
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm('Bild wirklich löschen?')) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success('Bild gelöscht');
    } catch (e) {
      toast.error(`Löschen fehlgeschlagen: ${e.response?.data?.detail || e.message}`);
    }
  };

  const swapOrder = async (idx, direction) => {
    const target = idx + direction;
    if (target < 0 || target >= items.length) return;
    const a = items[idx]; const b = items[target];
    const reordered = [...items];
    reordered[idx] = b; reordered[target] = a;
    const final = reordered.map((it, i) => ({ ...it, order: (i + 1) * 10 }));
    setItems(final);
    try {
      await api.patch('/admin/gallery/reorder', { items: final.map((i) => ({ id: i.id, order: i.order })) });
    } catch (e) {
      toast.error(`Reihenfolge nicht gespeichert: ${e.response?.data?.detail || e.message}`);
      load();
    }
  };

  const triggerUpload = (id) => {
    replaceTargetId.current = id;
    fileInputRef.current?.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const targetId = replaceTargetId.current;
    const isNew = targetId === '__new__';

    const ALLOWED = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'];
    const ext = file.name.split('.').pop().toLowerCase();
    if (!ALLOWED.includes(ext)) {
      toast.error(`Dateityp .${ext} nicht erlaubt. Erlaubt: JPG, PNG, WEBP, HEIC`);
      e.target.value = '';
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error('Datei zu groß (max 15 MB)');
      e.target.value = '';
      return;
    }

    setUploadingId(isNew ? '__new__' : targetId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const up = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const url = `${apiBase}/api/files/${up.data.id}`;

      if (isNew) {
        const res = await api.post('/admin/gallery', {
          image_url: url,
          alt_text: file.name,
          active: true,
          order: 0,
        });
        setItems((prev) => [...prev, res.data]);
        toast.success('Bild hinzugefügt');
      } else {
        const res = await api.put(`/admin/gallery/${targetId}`, { image_url: url });
        updateLocal(targetId, res.data);
        toast.success('Bild ersetzt');
      }
    } catch (err) {
      toast.error(`Upload fehlgeschlagen: ${err.response?.data?.detail || err.message}`);
    } finally {
      setUploadingId(null);
      e.target.value = '';
      replaceTargetId.current = null;
    }
  };

  const handleAddNew = () => {
    setCreating(true);
    replaceTargetId.current = '__new__';
    fileInputRef.current?.click();
    setTimeout(() => setCreating(false), 500);
  };

  return (
    <AdminLayout title="Galerie verwalten">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        onChange={handleFile}
        className="hidden"
        data-testid="gallery-file-input"
      />

      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-sm text-gray-500">
            {items.filter((i) => i.active).length} aktive · {items.length} gesamt
          </p>
        </div>
        <Button
          onClick={handleAddNew}
          disabled={creating || loadError !== null}
          className="bg-[#4A331D] hover:bg-[#593B23]"
          data-testid="gallery-add-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Bild hinzufügen
        </Button>
      </div>

      {loadError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 mb-6" data-testid="gallery-load-error">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-red-800 mb-1">Galerie konnte nicht geladen werden</h3>
              <p className="text-sm text-red-700 mb-3">{loadError.message}</p>
              <details className="text-xs text-red-600">
                <summary className="cursor-pointer">Technische Details</summary>
                <pre className="mt-2 p-2 bg-white border rounded overflow-x-auto whitespace-pre-wrap break-all">
{`Status: ${loadError.status || 'kein Response'}
URL:    ${loadError.url}
Detail: ${loadError.detail}`}
                </pre>
              </details>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={load} data-testid="gallery-retry-btn">
                  <RefreshCw className="w-3 h-3 mr-1" /> Erneut versuchen
                </Button>
                {loadError.status === 401 && (
                  <Button size="sm" variant="outline" onClick={() => { window.location.href = '/admin/login'; }}>
                    Neu einloggen
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </div>
      ) : !loadError && items.length === 0 ? (
        <div className="bg-white rounded-xl p-10 text-center text-gray-500 border border-dashed" data-testid="gallery-empty">
          <ImageIcon className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p className="font-medium text-gray-700 mb-1">Noch keine Galerie-Bilder</p>
          <p className="text-sm">Klicken Sie auf „Bild hinzufügen" um zu starten.</p>
        </div>
      ) : !loadError && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {items.map((it, idx) => (
            <div
              key={it.id}
              className={`bg-white rounded-xl border p-4 space-y-3 ${it.active ? 'border-gray-200' : 'border-orange-200 bg-orange-50/30'}`}
              data-testid={`gallery-item-${it.id}`}
            >
              <div className="flex gap-3">
                <div className="relative w-32 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 border">
                  {it.image_url ? (
                    <img
                      src={it.image_url}
                      alt={it.alt_text || ''}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <ImageIcon className="w-8 h-8" />
                    </div>
                  )}
                  <button
                    onClick={() => triggerUpload(it.id)}
                    disabled={uploadingId === it.id}
                    className="absolute inset-0 bg-black/0 hover:bg-black/40 text-white text-xs font-medium opacity-0 hover:opacity-100 transition-all flex items-center justify-center"
                    data-testid={`gallery-replace-${it.id}`}
                  >
                    {uploadingId === it.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                      <span className="flex items-center gap-1"><Upload className="w-3 h-3" /> Ersetzen</span>
                    )}
                  </button>
                </div>

                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={!!it.active}
                      onCheckedChange={(v) => updateLocal(it.id, { active: v })}
                      data-testid={`gallery-active-${it.id}`}
                    />
                    <span className={`text-xs font-medium flex items-center gap-1 ${it.active ? 'text-green-700' : 'text-orange-700'}`}>
                      {it.active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {it.active ? 'Sichtbar' : 'Versteckt'}
                    </span>
                    <span className="text-xs text-gray-400 ml-auto">Pos. {it.order ?? 0}</span>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => swapOrder(idx, -1)} disabled={idx === 0} className="h-7 px-2">
                      <ArrowUp className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => swapOrder(idx, 1)} disabled={idx === items.length - 1} className="h-7 px-2">
                      <ArrowDown className="w-3 h-3" />
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => removeItem(it.id)}
                      className="h-7 px-2 text-red-600 border-red-200 hover:bg-red-50 ml-auto"
                      data-testid={`gallery-delete-${it.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Titel</Label>
                  <Input
                    value={it.title || ''}
                    onChange={(e) => updateLocal(it.id, { title: e.target.value })}
                    placeholder="z.B. Badsanierung Köln"
                    className="h-8 text-sm"
                    data-testid={`gallery-title-${it.id}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">Kategorie</Label>
                  <select
                    value={it.category || ''}
                    onChange={(e) => updateLocal(it.id, { category: e.target.value })}
                    className="w-full h-8 text-sm border border-gray-200 rounded-md bg-white px-2"
                    data-testid={`gallery-category-${it.id}`}
                  >
                    <option value="">— keine —</option>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <Label className="text-xs">Alt-Text (SEO)</Label>
                <Input
                  value={it.alt_text || ''}
                  onChange={(e) => updateLocal(it.id, { alt_text: e.target.value })}
                  placeholder="Bildbeschreibung für Suchmaschinen"
                  className="h-8 text-sm"
                  data-testid={`gallery-alt-${it.id}`}
                />
              </div>

              <div>
                <Label className="text-xs">Beschreibung (optional)</Label>
                <Textarea
                  value={it.description || ''}
                  onChange={(e) => updateLocal(it.id, { description: e.target.value })}
                  rows={2}
                  className="text-sm resize-none"
                  data-testid={`gallery-desc-${it.id}`}
                />
              </div>

              <Button
                onClick={() => persist(it.id)}
                disabled={savingId === it.id}
                size="sm"
                className="w-full bg-[#4A331D] hover:bg-[#593B23] h-8"
                data-testid={`gallery-save-${it.id}`}
              >
                {savingId === it.id ? <Loader2 className="w-3 h-3 animate-spin mr-2" /> : <Save className="w-3 h-3 mr-2" />}
                Speichern
              </Button>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminGallery;
